import {
  UNASSIGNED_CONSULTING_ROOM_RESOURCE_ID,
  UNASSIGNED_CONSULTING_ROOM_RESOURCE_LABEL,
} from "./appointmentUnassignedConsultingRoom";
import {
  UNASSIGNED_DOCTOR_RESOURCE_ID,
  UNASSIGNED_DOCTOR_RESOURCE_LABEL,
} from "./appointmentUnassignedDoctor";
import {
  stripSchedulerResourceDisplayLabel,
  stripUnassignedDoctorSortPrefix,
} from "./appointmentDoctorSelect";

import { isFrontendVisibilityOff } from "@helpers/employeeFrontendVisibility";
import { positionAppointmentHoverTooltip } from "./appointmentEventDisplay";
import { Z_MODAL_BASE } from "@constants/zIndex";
/** localStorage key：`appointmentExcludedDoctorIds:${userId}:${brandId}:${branchId}`（排除名單） */
export const APPOINTMENT_EXCLUDED_DOCTOR_IDS_KEY_PREFIX =
  "appointmentExcludedDoctorIds";

/** 舊版白名單 key，讀取時會遷移到排除名單 */
export const APPOINTMENT_VISIBLE_DOCTOR_IDS_KEY_PREFIX =
  "appointmentVisibleDoctorIds";

/** localStorage key 前綴：`appointmentHideArchivedDoctors:${userId}` */
export const APPOINTMENT_HIDE_ARCHIVED_DOCTORS_KEY_PREFIX =
  "appointmentHideArchivedDoctors";

/** localStorage key 前綴：`appointmentForceShowFrontendHiddenDoctors:${userId}` */
export const APPOINTMENT_FORCE_SHOW_FRONTEND_HIDDEN_DOCTORS_KEY_PREFIX =
  "appointmentForceShowFrontendHiddenDoctors";

/** localStorage key 前綴：`appointmentOnlyDoctorsWithAppointments:${userId}`（相容舊 boolean；現為三元模式） */
export const APPOINTMENT_ONLY_DOCTORS_WITH_APPOINTMENTS_KEY_PREFIX =
  "appointmentOnlyDoctorsWithAppointments";

/** 醫師欄篩選：有預約／有值班／全部 */
export const APPOINTMENT_DOCTOR_RESOURCE_FILTER_MODES = {
  WITH_APPOINTMENTS: "withAppointments",
  ON_DUTY: "onDuty",
  ALL: "all",
};

export const APPOINTMENT_DOCTOR_RESOURCE_FILTER_MODE_OPTIONS = [
  {
    value: APPOINTMENT_DOCTOR_RESOURCE_FILTER_MODES.WITH_APPOINTMENTS,
    label: "有預約",
  },
  {
    value: APPOINTMENT_DOCTOR_RESOURCE_FILTER_MODES.ON_DUTY,
    label: "有值班",
  },
  {
    value: APPOINTMENT_DOCTOR_RESOURCE_FILTER_MODES.ALL,
    label: "全部",
  },
];

export const DEFAULT_APPOINTMENT_DOCTOR_RESOURCE_FILTER_MODE =
  APPOINTMENT_DOCTOR_RESOURCE_FILTER_MODES.WITH_APPOINTMENTS;

/** 診間欄篩選（二元）：有預約／全部 */
export const APPOINTMENT_ROOM_RESOURCE_FILTER_MODES = {
  WITH_APPOINTMENTS: "withAppointments",
  ALL: "all",
};

export const APPOINTMENT_ROOM_RESOURCE_FILTER_MODE_OPTIONS = [
  {
    value: APPOINTMENT_ROOM_RESOURCE_FILTER_MODES.WITH_APPOINTMENTS,
    label: "有預約",
  },
  {
    value: APPOINTMENT_ROOM_RESOURCE_FILTER_MODES.ALL,
    label: "全部",
  },
];

/** localStorage key：`appointmentExcludedConsultingRoomIds:${userId}:${brandId}:${branchId}`（排除名單） */
export const APPOINTMENT_EXCLUDED_CONSULTING_ROOM_IDS_KEY_PREFIX =
  "appointmentExcludedConsultingRoomIds";

/** 舊版白名單 key，讀取時會遷移到排除名單 */
export const APPOINTMENT_VISIBLE_CONSULTING_ROOM_IDS_KEY_PREFIX =
  "appointmentVisibleConsultingRoomIds";

/** 舊版全站共用 key，讀取時會遷移到依帳號 key */
export const LEGACY_APPOINTMENT_ONLY_DOCTORS_WITH_APPOINTMENTS_KEY =
  "appointmentOnlyDoctorsWithAppointments";

/** localStorage key 前綴：`appointmentOnlyConsultingRoomsWithAppointments:${userId}` */
export const APPOINTMENT_ONLY_CONSULTING_ROOMS_WITH_APPOINTMENTS_KEY_PREFIX =
  "appointmentOnlyConsultingRoomsWithAppointments";

const REVX_USER_ID_STORAGE_KEY = "revxId";

/** 目前登入帳號 id（與登入流程寫入的 revxId 一致） */
export function getCurrentAppointmentUserId() {
  if (typeof window === "undefined") return null;
  const id = localStorage.getItem(REVX_USER_ID_STORAGE_KEY);
  return id != null && id !== "" ? String(id) : null;
}

function formatAppointmentResourceScopeId(value) {
  if (value == null || value === "") return null;
  return String(value);
}

/** @typedef {{ userId?: string|null, brandId?: string|number|null, branchId?: string|number|null }} AppointmentResourceScope */

export function normalizeAppointmentResourceScope(
  userIdOrScope,
  brandId,
  branchId
) {
  if (userIdOrScope != null && typeof userIdOrScope === "object") {
    return getAppointmentResourceScope(userIdOrScope);
  }
  return getAppointmentResourceScope({ userId: userIdOrScope, brandId, branchId });
}

export function getAppointmentResourceScope({
  userId,
  brandId,
  branchId,
} = {}) {
  return {
    userId: formatAppointmentResourceScopeId(
      userId ?? getCurrentAppointmentUserId()
    ),
    brandId: formatAppointmentResourceScopeId(brandId),
    branchId: formatAppointmentResourceScopeId(branchId),
  };
}

export function buildAppointmentResourceStorageKey(prefix, scope) {
  const { userId, brandId, branchId } = getAppointmentResourceScope(scope);
  if (!userId || !brandId || !branchId) return null;
  return `${prefix}:${userId}:${brandId}:${branchId}`;
}

/** 舊版僅依 userId 的 key（讀取時 best-effort 遷移到目前品牌＋分店） */
export function buildLegacyUserOnlyAppointmentResourceStorageKey(prefix, userId) {
  const uid = formatAppointmentResourceScopeId(
    userId ?? getCurrentAppointmentUserId()
  );
  if (!uid) return null;
  return `${prefix}:${uid}`;
}

export function getAppointmentExcludedDoctorIdsStorageKey(
  userIdOrScope = getCurrentAppointmentUserId(),
  brandId,
  branchId
) {
  return buildAppointmentResourceStorageKey(
    APPOINTMENT_EXCLUDED_DOCTOR_IDS_KEY_PREFIX,
    normalizeAppointmentResourceScope(userIdOrScope, brandId, branchId)
  );
}

export function getAppointmentVisibleDoctorIdsStorageKey(
  userIdOrScope = getCurrentAppointmentUserId(),
  brandId,
  branchId
) {
  return buildAppointmentResourceStorageKey(
    APPOINTMENT_VISIBLE_DOCTOR_IDS_KEY_PREFIX,
    normalizeAppointmentResourceScope(userIdOrScope, brandId, branchId)
  );
}

export function getAppointmentOnlyDoctorsWithAppointmentsStorageKey(
  userId = getCurrentAppointmentUserId()
) {
  if (userId == null || userId === "") return null;
  return `${APPOINTMENT_ONLY_DOCTORS_WITH_APPOINTMENTS_KEY_PREFIX}:${String(
    userId
  )}`;
}

export function getAppointmentOnlyConsultingRoomsWithAppointmentsStorageKey(
  userId = getCurrentAppointmentUserId()
) {
  if (userId == null || userId === "") return null;
  return `${APPOINTMENT_ONLY_CONSULTING_ROOMS_WITH_APPOINTMENTS_KEY_PREFIX}:${String(
    userId
  )}`;
}

export function getAppointmentExcludedConsultingRoomIdsStorageKey(
  userIdOrScope = getCurrentAppointmentUserId(),
  brandId,
  branchId
) {
  return buildAppointmentResourceStorageKey(
    APPOINTMENT_EXCLUDED_CONSULTING_ROOM_IDS_KEY_PREFIX,
    normalizeAppointmentResourceScope(userIdOrScope, brandId, branchId)
  );
}

export function getAppointmentVisibleConsultingRoomIdsStorageKey(
  userIdOrScope = getCurrentAppointmentUserId(),
  brandId,
  branchId
) {
  return buildAppointmentResourceStorageKey(
    APPOINTMENT_VISIBLE_CONSULTING_ROOM_IDS_KEY_PREFIX,
    normalizeAppointmentResourceScope(userIdOrScope, brandId, branchId)
  );
}

export function getAppointmentHideArchivedDoctorsStorageKey(
  userId = getCurrentAppointmentUserId()
) {
  if (userId == null || userId === "") return null;
  return `${APPOINTMENT_HIDE_ARCHIVED_DOCTORS_KEY_PREFIX}:${String(userId)}`;
}

export function getAppointmentForceShowFrontendHiddenDoctorsStorageKey(
  userId = getCurrentAppointmentUserId()
) {
  if (userId == null || userId === "") return null;
  return `${APPOINTMENT_FORCE_SHOW_FRONTEND_HIDDEN_DOCTORS_KEY_PREFIX}:${String(
    userId
  )}`;
}

export const ARCHIVED_DOCTOR_NAME_SUFFIX = "(已封存)";

/** 已封存醫師顯示名稱，例如：王小明(已封存) */
export function formatDoctorDisplayName(name, isArchived) {
  const base = String(name ?? "").trim();
  if (!isArchived || !base) return base;
  if (base.endsWith(ARCHIVED_DOCTOR_NAME_SUFFIX)) return base;
  return `${base}${ARCHIVED_DOCTOR_NAME_SUFFIX}`;
}

function escapeDoctorGroupHtml(text) {
  return String(text ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** 行事曆醫師分欄 header 模板（已封存標籤於渲染後注入） */
export function buildDoctorGroupTemplateHtml() {
  return `
        <div class="custom-group-content d-flex align-items-center">
          <div class="details">
            <img src="{{image}}" alt="Doctor Avatar" data-lazy-image="true"></img>
          </div>
          <div class="details ms-3 custom-group-content__info">
            <div class="name">{{label}}</div>
          </div>
        </div>`;
}

function normalizeDoctorAvatarImageUrl(url) {
  if (!url) return "";
  if (String(url).startsWith("data:")) return String(url);
  if (typeof window === "undefined") return String(url);
  try {
    return new URL(String(url), window.location.href).href;
  } catch {
    return String(url);
  }
}

function getMountedDoctorAvatarImageUrl(container) {
  if (!container) return "";
  const mount = container.querySelector(".appointment-doctor-avatar-mount");
  const mountedImg = mount?.querySelector("img");
  if (mountedImg) {
    return normalizeDoctorAvatarImageUrl(
      mountedImg.getAttribute("src") || mountedImg.src
    );
  }
  const lazyImg = container.querySelector("img[data-lazy-image='true']");
  if (lazyImg) {
    return normalizeDoctorAvatarImageUrl(
      lazyImg.getAttribute("src") || lazyImg.src
    );
  }
  return "";
}

function buildDoctorGroupDisplayTooltip(baseName, isArchived) {
  const name = String(baseName ?? "").trim();
  if (!name) return "";
  return isArchived ? `${name}${ARCHIVED_DOCTOR_NAME_SUFFIX}` : name;
}

function applyDoctorGroupNameTooltip(nameEl, baseName, isArchived) {
  if (!nameEl) return;
  const tooltip = buildDoctorGroupDisplayTooltip(baseName, isArchived);
  const nameSpan = nameEl.querySelector(".appointment-doctor-archived-name");
  nameEl.removeAttribute("title");
  if (nameSpan) {
    if (tooltip) {
      nameSpan.setAttribute("data-full-name", tooltip);
    } else {
      nameSpan.removeAttribute("data-full-name");
    }
    nameSpan.removeAttribute("title");
  }
}

function updateDoctorGroupNameContent(nameEl, baseName, isArchived) {
  if (!nameEl) return;
  const suffixHtml = isArchived
    ? `<span class="appointment-doctor-archived-suffix">${ARCHIVED_DOCTOR_NAME_SUFFIX}</span>`
    : "";
  nameEl.innerHTML = `<span class="appointment-doctor-archived-name">${escapeDoctorGroupHtml(baseName)}</span>${suffixHtml}`;
  applyDoctorGroupNameTooltip(nameEl, baseName, isArchived);
}

/** 解析 Smart Scheduler 分欄標題（含虛擬醫師／診間資源） */
export function resolveSchedulerGroupDisplayName(cellObj) {
  if (cellObj == null) return "";

  const rawId =
    cellObj?.id ??
    cellObj?.value ??
    cellObj?.consulting_room_id ??
    cellObj?.doctorId ??
    (typeof cellObj === "number" || typeof cellObj === "string" ? cellObj : null);
  const resourceId = Number(rawId);

  if (!Number.isNaN(resourceId)) {
    if (resourceId === UNASSIGNED_DOCTOR_RESOURCE_ID) {
      return UNASSIGNED_DOCTOR_RESOURCE_LABEL;
    }
    if (resourceId === UNASSIGNED_CONSULTING_ROOM_RESOURCE_ID) {
      return UNASSIGNED_CONSULTING_ROOM_RESOURCE_LABEL;
    }
  }

  // doctorName 為乾淨顯示名；name/label 可能帶 \uFFFF 供 Scheduler 排最後
  return stripDoctorDisplayNameSuffix(
    stripUnassignedDoctorSortPrefix(
      cellObj?.doctorName ??
        cellObj?.name ??
        cellObj?.text ??
        cellObj?.label ??
        ""
    )
  );
}

/** Smart Scheduler groupTemplate 函式：直接渲染姓名與 (已封存)，避免 refresh 後閃爍 */
export function renderDoctorGroupTemplateCell(cellContent, cellObj) {
  if (!cellContent || !cellObj) return;

  const doctorId = cellObj.id ?? cellObj.value;
  const baseName = resolveSchedulerGroupDisplayName(cellObj);
  const image = cellObj.image ?? "";
  const isArchived = !!cellObj.is_archived;
  const suffixHtml = isArchived
    ? `<span class="appointment-doctor-archived-suffix">${ARCHIVED_DOCTOR_NAME_SUFFIX}</span>`
    : "";
  const nextImageUrl = normalizeDoctorAvatarImageUrl(image);
  const existingContent = cellContent.querySelector(".custom-group-content");
  const existingImageUrl = getMountedDoctorAvatarImageUrl(cellContent);

  // 頭像已掛載且 URL 未變時，只更新姓名／封存標籤，避免點擊預約後整欄重建造成閃爍
  if (
    existingContent &&
    existingImageUrl &&
    existingImageUrl === nextImageUrl
  ) {
    existingContent.setAttribute(
      "data-doctor-id",
      escapeDoctorGroupHtml(String(doctorId ?? ""))
    );
    updateDoctorGroupNameContent(
      existingContent.querySelector(".name"),
      baseName,
      isArchived
    );
    return;
  }

  const fullNameTooltip = buildDoctorGroupDisplayTooltip(baseName, isArchived);
  const dataFullNameAttr = fullNameTooltip
    ? ` data-full-name="${escapeDoctorGroupHtml(fullNameTooltip)}"`
    : "";

  cellContent.innerHTML = `
    <div class="custom-group-content d-flex align-items-center" data-doctor-id="${escapeDoctorGroupHtml(String(doctorId ?? ""))}">
      <div class="details">
        <img src="${escapeDoctorGroupHtml(image)}" alt="Doctor Avatar" data-lazy-image="true"></img>
      </div>
      <div class="details ms-3 custom-group-content__info">
        <div class="name">
          <span class="appointment-doctor-archived-name"${dataFullNameAttr}>${escapeDoctorGroupHtml(baseName)}</span>${suffixHtml}
        </div>
      </div>
    </div>`;
}

/** 移除顯示名稱上的「(已封存)」後綴與排序前綴，供比對醫師用 */
export function stripDoctorDisplayNameSuffix(name) {
  const base = stripSchedulerResourceDisplayLabel(String(name ?? "").trim());
  if (base.endsWith(ARCHIVED_DOCTOR_NAME_SUFFIX)) {
    return base.slice(0, -ARCHIVED_DOCTOR_NAME_SUFFIX.length).trim();
  }
  return base;
}

/** 僅取左側醫師分欄 header，避免 horizontal header 重複節點造成 index 錯位 */
export function querySchedulerDoctorGroupContents(schedulerRoot) {
  if (!schedulerRoot) return [];
  const scoped = [
    ...schedulerRoot.querySelectorAll(
      ".smart-scheduler-groups-container .custom-group-content"
    ),
  ];
  if (scoped.length) return scoped;
  return [
    ...schedulerRoot.querySelectorAll(
      ".smart-scheduler-group-header .custom-group-content"
    ),
  ];
}

const SCHEDULER_GROUP_HEADER_TOOLTIP_BOUND =
  "data-appointment-group-header-tooltip-bound";

function resolveSchedulerGroupHeaderFullName(host) {
  if (!host) return "";
  const nameSpan = host.querySelector(".appointment-doctor-archived-name");
  return (
    nameSpan?.getAttribute("data-full-name")?.trim() ||
    nameSpan?.textContent?.trim() ||
    ""
  );
}

function isSchedulerGroupHeaderNameTruncated(host) {
  const nameSpan = host.querySelector(".appointment-doctor-archived-name");
  if (!nameSpan) return false;
  return nameSpan.scrollWidth > nameSpan.clientWidth + 1;
}

/** 分欄標題 hover：與預約色塊相同樣式，僅在名稱被 ellipsis 時顯示全名 */
export function bindSchedulerGroupHeaderTooltips(schedulerRoot) {
  if (
    !schedulerRoot ||
    schedulerRoot.getAttribute(SCHEDULER_GROUP_HEADER_TOOLTIP_BOUND)
  ) {
    return undefined;
  }

  schedulerRoot.setAttribute(SCHEDULER_GROUP_HEADER_TOOLTIP_BOUND, "true");

  let tooltipEl = null;
  let moveHandler = null;
  let activeHost = null;

  const hideGroupHeaderTooltip = () => {
    if (moveHandler) {
      document.removeEventListener("mousemove", moveHandler);
      moveHandler = null;
    }
    tooltipEl?.remove();
    tooltipEl = null;
    activeHost = null;
  };

  const showGroupHeaderTooltip = (event, host) => {
    hideGroupHeaderTooltip();
    const fullText = resolveSchedulerGroupHeaderFullName(host);
    if (!fullText || !isSchedulerGroupHeaderNameTruncated(host)) return;

    const tooltip = document.createElement("div");
    tooltip.className =
      "custom-tooltip appointment-hover-tooltip appointment-group-header-tooltip";
    tooltip.textContent = fullText;
    Object.assign(tooltip.style, {
      position: "fixed",
      zIndex: String(Z_MODAL_BASE),
      pointerEvents: "none",
    });
    document.body.appendChild(tooltip);

    const place = (x, y) => positionAppointmentHoverTooltip(tooltip, x, y);
    place(event.clientX, event.clientY);
    requestAnimationFrame(() => place(event.clientX, event.clientY));

    moveHandler = (moveEvent) => place(moveEvent.clientX, moveEvent.clientY);
    document.addEventListener("mousemove", moveHandler);
    tooltipEl = tooltip;
  };

  const headerContentSelector =
    ".smart-scheduler-groups-container .custom-group-content, .smart-scheduler-view-header-horizontal .custom-group-content, .smart-scheduler-group-header .custom-group-content";

  const onMouseOver = (event) => {
    const host = event.target.closest(headerContentSelector);
    if (!host || !schedulerRoot.contains(host)) return;
    if (activeHost === host) return;
    activeHost = host;
    showGroupHeaderTooltip(event, host);
  };

  const onMouseOut = (event) => {
    const host = event.target.closest(headerContentSelector);
    if (!host) return;
    const related = event.relatedTarget;
    if (related && host.contains(related)) return;
    hideGroupHeaderTooltip();
  };

  schedulerRoot.addEventListener("mouseover", onMouseOver);
  schedulerRoot.addEventListener("mouseout", onMouseOut);

  return () => {
    hideGroupHeaderTooltip();
    schedulerRoot.removeEventListener("mouseover", onMouseOver);
    schedulerRoot.removeEventListener("mouseout", onMouseOut);
    schedulerRoot.removeAttribute(SCHEDULER_GROUP_HEADER_TOOLTIP_BOUND);
  };
}

const DOCTOR_GROUP_AVATAR_IMG_SELECTOR =
  ".smart-scheduler-groups-container img[data-lazy-image='true'], .smart-scheduler-group-header img[data-lazy-image='true']";

/** 供頭像 lazy load 使用的 img 選擇器 */
export function getDoctorGroupAvatarImgSelector() {
  return DOCTOR_GROUP_AVATAR_IMG_SELECTOR;
}

/**
 * 從分欄 DOM 解析醫師 id（優先 data-doctor-id，其次比對名稱；不依賴 DOM 順序 index）
 */
export function resolveDoctorIdFromGroupContent(
  content,
  doctorColumns = [],
  employees = []
) {
  if (!content) return null;

  let doctorId = Number(content.getAttribute("data-doctor-id"));
  if (!Number.isNaN(doctorId) && doctorId !== 0) return doctorId;

  const parentWithId = content.closest("[data-doctor-id]");
  if (parentWithId) {
    doctorId = Number(parentWithId.getAttribute("data-doctor-id"));
    if (!Number.isNaN(doctorId) && doctorId !== 0) return doctorId;
  }

  const nameEl = content.querySelector(".name");
  const displayName = stripDoctorDisplayNameSuffix(nameEl?.textContent || "");
  if (displayName) {
    const columnMatch = doctorColumns.find(
      (doctor) =>
        doctor.name === displayName ||
        stripDoctorDisplayNameSuffix(doctor.name) === displayName
    );
    if (columnMatch) return columnMatch.id;

    const employeeMatch = employees.find(
      (emp) =>
        emp.job_title === "DOCTOR" &&
        (emp.name === displayName ||
          formatDoctorDisplayName(emp.name, emp.is_archived) === displayName)
    );
    if (employeeMatch) return Number(employeeMatch.id);
  }

  return null;
}

/** 同步醫師分欄 data-doctor-id（以內容比對為主，index 僅在節點數與分欄數一致時後援） */
export function syncSchedulerDoctorGroupIds(
  schedulerRoot,
  doctorColumns = [],
  employees = []
) {
  if (!schedulerRoot) return;

  const contents = querySchedulerDoctorGroupContents(schedulerRoot);
  const indexFallbackEnabled =
    contents.length > 0 && contents.length === doctorColumns.length;

  contents.forEach((content, index) => {
    const doctorId =
      resolveDoctorIdFromGroupContent(content, doctorColumns, employees) ??
      (indexFallbackEnabled ? doctorColumns[index]?.id : null);

    if (doctorId == null || Number.isNaN(Number(doctorId)) || Number(doctorId) === 0) {
      return;
    }

    content.setAttribute("data-doctor-id", String(doctorId));
    const header = content.closest(".smart-scheduler-group-header");
    if (header) {
      header.setAttribute("data-doctor-id", String(doctorId));
    }
  });
}

/** 於 Smart Scheduler 醫師分欄標示已封存（名稱旁紅色「(已封存)」） */
export function applyArchivedDoctorNameLabel(nameEl, doctor) {
  if (!nameEl || !doctor) return;

  const baseName = String(doctor.name ?? "").trim();
  const nameSpan = nameEl.querySelector(".appointment-doctor-archived-name");
  const suffixEl = nameEl.querySelector(".appointment-doctor-archived-suffix");
  const hasArchivedSuffix = !!suffixEl;

  if (
    nameSpan &&
    nameSpan.textContent?.trim() === baseName &&
    hasArchivedSuffix === !!doctor.is_archived
  ) {
    applyDoctorGroupNameTooltip(nameEl, baseName, !!doctor.is_archived);
    return;
  }

  // 舊版將 suffix 插在 .name 外層，升級時先清掉
  const info = nameEl.parentElement;
  info?.querySelector(":scope > .appointment-doctor-archived-suffix")?.remove();

  nameEl.textContent = "";

  const nextNameSpan = document.createElement("span");
  nextNameSpan.className = "appointment-doctor-archived-name";
  nextNameSpan.textContent = baseName;
  nameEl.appendChild(nextNameSpan);

  if (doctor.is_archived) {
    const nextSuffixEl = document.createElement("span");
    nextSuffixEl.className = "appointment-doctor-archived-suffix";
    nextSuffixEl.textContent = ARCHIVED_DOCTOR_NAME_SUFFIX;
    nameEl.appendChild(nextSuffixEl);
  }

  applyDoctorGroupNameTooltip(nameEl, baseName, !!doctor.is_archived);
}

/** 於 Smart Scheduler 醫師分欄標示已封存（名稱旁紅色「(已封存)」） */
export function patchArchivedDoctorLabels(
  schedulerRoot,
  employees = [],
  doctorColumns = []
) {
  if (!schedulerRoot) return;

  const doctorById = new Map(
    employees
      .filter((emp) => emp.job_title === "DOCTOR")
      .map((emp) => [Number(emp.id), emp])
  );

  const contents = querySchedulerDoctorGroupContents(schedulerRoot);
  const indexFallbackEnabled =
    contents.length > 0 && contents.length === doctorColumns.length;

  contents.forEach((content, index) => {
    const info = content.querySelector(".custom-group-content__info") || content;
    const nameEl = info.querySelector(".name");
    if (!nameEl) return;

    let doctorId =
      resolveDoctorIdFromGroupContent(content, doctorColumns, employees) ??
      (indexFallbackEnabled ? doctorColumns[index]?.id : null);
    doctorId = Number(doctorId);

    const doctor = doctorById.get(doctorId);
    if (!doctor) return;

    applyArchivedDoctorNameLabel(nameEl, doctor);
  });
}

/** 補齊分欄標題（虛擬診間／醫師等 Smart Scheduler 未帶 label 時） */
export function patchSchedulerGroupColumnLabels(
  schedulerRoot,
  columns = []
) {
  if (!schedulerRoot || !columns.length) return;

  const columnById = new Map(
    columns.map((column) => [Number(column.id), column])
  );
  const contents = querySchedulerDoctorGroupContents(schedulerRoot);
  const indexFallbackEnabled =
    contents.length > 0 && contents.length === columns.length;

  contents.forEach((content, index) => {
    const info = content.querySelector(".custom-group-content__info") || content;
    const nameEl = info.querySelector(".name");
    if (!nameEl) return;

    let resourceId = Number(content.getAttribute("data-doctor-id"));
    if (Number.isNaN(resourceId) && indexFallbackEnabled) {
      resourceId = Number(columns[index]?.id);
    }

    const column = !Number.isNaN(resourceId)
      ? columnById.get(resourceId)
      : indexFallbackEnabled
        ? columns[index]
        : null;
    const displayName =
      resolveSchedulerGroupDisplayName({
        id: resourceId,
        label: column?.name,
        name: column?.name,
      }) || column?.name || "";

    if (!displayName) return;

    const nameSpan = nameEl.querySelector(".appointment-doctor-archived-name");
    const currentName =
      nameSpan?.textContent?.trim() || nameEl.textContent?.trim() || "";
    const isArchived = !!column?.is_archived;
    if (currentName === displayName) {
      applyDoctorGroupNameTooltip(nameEl, displayName, isArchived);
      return;
    }

    updateDoctorGroupNameContent(nameEl, displayName, isArchived);
    if (!Number.isNaN(resourceId)) {
      content.setAttribute("data-doctor-id", String(resourceId));
      const header = content.closest(".smart-scheduler-group-header");
      if (header) {
        header.setAttribute("data-doctor-id", String(resourceId));
      }
    }
  });
}

/** 依「隱藏已封存醫師」設定過濾醫師清單 */
export function filterDoctorsByArchivedPreference(doctors = [], hideArchived) {
  if (!hideArchived) return doctors;
  return doctors.filter((doctor) => !doctor.is_archived);
}

/**
 * 依「強制顯示隱藏前台顯示醫師」設定過濾醫師清單。
 * 預設不顯示關閉前台顯示者；開啟強制顯示後才納入。
 *
 * 與「顯示已封存醫師」同時存在時：
 * - 已封存醫師只由「顯示已封存醫師」決定（強制顯示無法單獨叫出已封存醫師）
 * - 因此「已封存＋關閉前台顯示」者：顯示已封存開 → 顯示；顯示已封存關 → 不顯示
 * 虛擬「不指定醫師」欄不受前台顯示影響。
 */
export function filterDoctorsByFrontendVisibilityPreference(
  doctors = [],
  forceShowFrontendHidden = false
) {
  if (forceShowFrontendHidden) return doctors;
  return doctors.filter(
    (doctor) =>
      Number(doctor?.id) === UNASSIGNED_DOCTOR_RESOURCE_ID ||
      !isFrontendVisibilityOff(doctor) ||
      !!doctor?.is_archived
  );
}

/** 取得醫師篩選器可選的醫師 id（只含團隊成員，不含前端假「不指定醫師」） */
export function getSelectableDoctorIds(
  employees = [],
  hideArchived = true,
  forceShowFrontendHidden = false
) {
  // 先套用封存，再套用前台顯示（已封存者不受「強制顯示」擋下）
  return filterDoctorsByFrontendVisibilityPreference(
    filterDoctorsByArchivedPreference(
      employees.filter((emp) => emp.job_title === "DOCTOR"),
      hideArchived
    ),
    forceShowFrontendHidden
  ).map((emp) => emp.id);
}

function parseDoctorIdList(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed.map((id) => Number(id)).filter((id) => !Number.isNaN(id));
  } catch (err) {
    console.log("Error parsing doctor id list", err);
    return null;
  }
}

/** 依目前可選清單與勾選結果，算出要排除的醫師 id */
export function computeExcludedDoctorIds(allDoctorIds, visibleDoctorIds) {
  const allSet = new Set(allDoctorIds.map((id) => Number(id)));
  const visibleSet = new Set(visibleDoctorIds.map((id) => Number(id)));
  return [...allSet].filter((id) => !visibleSet.has(id));
}

/** 依排除名單解析要顯示的醫師 id；excluded === null 表示尚未設定（預設全選） */
export function resolveVisibleDoctorIdsFromExcluded(
  allDoctorIds,
  excludedDoctorIds
) {
  const allSet = new Set(allDoctorIds.map((id) => Number(id)));
  if (allSet.size === 0) return allSet;
  if (excludedDoctorIds === null) return allSet;

  const excludedSet = new Set(excludedDoctorIds.map((id) => Number(id)));
  return new Set([...allSet].filter((id) => !excludedSet.has(id)));
}

function migrateLegacyWhitelistToExcluded(whitelist, allDoctorIds) {
  const allSet = new Set(allDoctorIds.map((id) => Number(id)));
  const saved = whitelist.map((id) => Number(id)).filter((id) => !Number.isNaN(id));

  if (saved.length === 0) {
    return [...allSet];
  }

  return [...allSet].filter((id) => !saved.includes(id));
}

export function readExcludedDoctorIdsFromStorage(
  excludedKey,
  legacyKey,
  allDoctorIds = []
) {
  if (typeof window === "undefined") return null;
  if (!excludedKey) return null;

  const rawExcluded = localStorage.getItem(excludedKey);
  if (rawExcluded !== null) {
    return parseDoctorIdList(rawExcluded) ?? [];
  }

  if (!legacyKey) return null;
  const rawLegacy = localStorage.getItem(legacyKey);
  if (rawLegacy === null) return null;

  const whitelist = parseDoctorIdList(rawLegacy);
  if (whitelist === null) return null;

  const migrated = migrateLegacyWhitelistToExcluded(whitelist, allDoctorIds);
  localStorage.setItem(
    excludedKey,
    JSON.stringify(migrated.map((id) => Number(id)))
  );
  localStorage.removeItem(legacyKey);
  return migrated;
}

function removeLegacyUserOnlyAppointmentResourceKeys(
  excludedPrefix,
  visiblePrefix,
  userId
) {
  if (typeof window === "undefined") return;
  const legacyExcluded = buildLegacyUserOnlyAppointmentResourceStorageKey(
    excludedPrefix,
    userId
  );
  const legacyVisible = buildLegacyUserOnlyAppointmentResourceStorageKey(
    visiblePrefix,
    userId
  );
  if (legacyExcluded) localStorage.removeItem(legacyExcluded);
  if (legacyVisible) localStorage.removeItem(legacyVisible);
}

function readSavedExcludedResourceIds({
  scope,
  excludedPrefix,
  visiblePrefix,
  getExcludedKey,
  getVisibleKey,
  persistExcluded,
  allIds,
}) {
  const normalizedScope = getAppointmentResourceScope(scope);
  const scopedExcludedKey = getExcludedKey(normalizedScope);
  const scopedVisibleKey = getVisibleKey(normalizedScope);

  if (scopedExcludedKey) {
    const fromScoped = readExcludedDoctorIdsFromStorage(
      scopedExcludedKey,
      scopedVisibleKey,
      allIds
    );
    if (fromScoped !== null) return fromScoped;
  }

  const legacyExcludedKey = buildLegacyUserOnlyAppointmentResourceStorageKey(
    excludedPrefix,
    normalizedScope.userId
  );
  const legacyVisibleKey = buildLegacyUserOnlyAppointmentResourceStorageKey(
    visiblePrefix,
    normalizedScope.userId
  );
  const fromLegacy = readExcludedDoctorIdsFromStorage(
    legacyExcludedKey,
    legacyVisibleKey,
    allIds
  );
  if (fromLegacy === null || !scopedExcludedKey) return fromLegacy;

  persistExcluded(fromLegacy, normalizedScope);
  removeLegacyUserOnlyAppointmentResourceKeys(
    excludedPrefix,
    visiblePrefix,
    normalizedScope.userId
  );
  return fromLegacy;
}

export function readSavedExcludedDoctorIds(
  userIdOrScope = getCurrentAppointmentUserId(),
  allDoctorIds = [],
  brandId,
  branchId
) {
  const scope = normalizeAppointmentResourceScope(
    userIdOrScope,
    brandId,
    branchId
  );
  return readSavedExcludedResourceIds({
    scope,
    excludedPrefix: APPOINTMENT_EXCLUDED_DOCTOR_IDS_KEY_PREFIX,
    visiblePrefix: APPOINTMENT_VISIBLE_DOCTOR_IDS_KEY_PREFIX,
    getExcludedKey: (s) => getAppointmentExcludedDoctorIdsStorageKey(s),
    getVisibleKey: (s) => getAppointmentVisibleDoctorIdsStorageKey(s),
    persistExcluded: persistExcludedDoctorIds,
    allIds: allDoctorIds,
  });
}

export function persistExcludedDoctorIds(
  excludedDoctorIds,
  userIdOrScope = getCurrentAppointmentUserId(),
  brandId,
  branchId
) {
  if (typeof window === "undefined") return;
  const scope = normalizeAppointmentResourceScope(
    userIdOrScope,
    brandId,
    branchId
  );
  const key = getAppointmentExcludedDoctorIdsStorageKey(scope);
  if (!key) return;
  localStorage.setItem(
    key,
    JSON.stringify(excludedDoctorIds.map((id) => Number(id)))
  );
  const legacyVisibleKey = getAppointmentVisibleDoctorIdsStorageKey(scope);
  if (legacyVisibleKey) {
    localStorage.removeItem(legacyVisibleKey);
  }
  removeLegacyUserOnlyAppointmentResourceKeys(
    APPOINTMENT_EXCLUDED_DOCTOR_IDS_KEY_PREFIX,
    APPOINTMENT_VISIBLE_DOCTOR_IDS_KEY_PREFIX,
    scope.userId
  );
}

export function persistVisibleDoctorIds(
  visibleDoctorIds,
  allDoctorIds,
  userIdOrScope = getCurrentAppointmentUserId(),
  brandId,
  branchId
) {
  persistExcludedDoctorIds(
    computeExcludedDoctorIds(allDoctorIds, visibleDoctorIds),
    userIdOrScope,
    brandId,
    branchId
  );
}

export function readSavedExcludedConsultingRoomIds(
  userIdOrScope = getCurrentAppointmentUserId(),
  allRoomIds = [],
  brandId,
  branchId
) {
  const scope = normalizeAppointmentResourceScope(
    userIdOrScope,
    brandId,
    branchId
  );
  return readSavedExcludedResourceIds({
    scope,
    excludedPrefix: APPOINTMENT_EXCLUDED_CONSULTING_ROOM_IDS_KEY_PREFIX,
    visiblePrefix: APPOINTMENT_VISIBLE_CONSULTING_ROOM_IDS_KEY_PREFIX,
    getExcludedKey: (s) => getAppointmentExcludedConsultingRoomIdsStorageKey(s),
    getVisibleKey: (s) => getAppointmentVisibleConsultingRoomIdsStorageKey(s),
    persistExcluded: persistExcludedConsultingRoomIds,
    allIds: allRoomIds,
  });
}

export function persistExcludedConsultingRoomIds(
  excludedRoomIds,
  userIdOrScope = getCurrentAppointmentUserId(),
  brandId,
  branchId
) {
  if (typeof window === "undefined") return;
  const scope = normalizeAppointmentResourceScope(
    userIdOrScope,
    brandId,
    branchId
  );
  const key = getAppointmentExcludedConsultingRoomIdsStorageKey(scope);
  if (!key) return;
  localStorage.setItem(
    key,
    JSON.stringify(excludedRoomIds.map((id) => Number(id)))
  );
  const legacyVisibleKey = getAppointmentVisibleConsultingRoomIdsStorageKey(scope);
  if (legacyVisibleKey) {
    localStorage.removeItem(legacyVisibleKey);
  }
  removeLegacyUserOnlyAppointmentResourceKeys(
    APPOINTMENT_EXCLUDED_CONSULTING_ROOM_IDS_KEY_PREFIX,
    APPOINTMENT_VISIBLE_CONSULTING_ROOM_IDS_KEY_PREFIX,
    scope.userId
  );
}

export function persistVisibleConsultingRoomIds(
  visibleRoomIds,
  allRoomIds,
  userIdOrScope = getCurrentAppointmentUserId(),
  brandId,
  branchId
) {
  persistExcludedConsultingRoomIds(
    computeExcludedDoctorIds(allRoomIds, visibleRoomIds),
    userIdOrScope,
    brandId,
    branchId
  );
}

/**
 * 解析要顯示的診間 id：預設全選；僅排除使用者明確取消勾選的診間。
 * 新增診間會自動顯示；已排除的診間若再次出現在清單中仍維持不選。
 */
export function resolveVisibleConsultingRoomIds(
  allRoomIds,
  userIdOrScope = getCurrentAppointmentUserId(),
  brandId,
  branchId
) {
  const scope = normalizeAppointmentResourceScope(
    userIdOrScope,
    brandId,
    branchId
  );
  const excluded = readSavedExcludedConsultingRoomIds(
    scope.userId,
    allRoomIds,
    scope.brandId,
    scope.branchId
  );
  return resolveVisibleDoctorIdsFromExcluded(allRoomIds, excluded);
}

export function readHideArchivedDoctorsPreference(
  userId = getCurrentAppointmentUserId()
) {
  if (typeof window === "undefined") return true;
  const key = getAppointmentHideArchivedDoctorsStorageKey(userId);
  if (!key) return true;
  const raw = localStorage.getItem(key);
  if (raw === null) return true;
  return raw === "true";
}

export function persistHideArchivedDoctorsPreference(
  checked,
  userId = getCurrentAppointmentUserId()
) {
  if (typeof window === "undefined") return;
  const key = getAppointmentHideArchivedDoctorsStorageKey(userId);
  if (!key) return;
  localStorage.setItem(key, String(checked));
}

/** 預設 false：行事曆不顯示關閉前台顯示的醫師 */
export function readForceShowFrontendHiddenDoctorsPreference(
  userId = getCurrentAppointmentUserId()
) {
  if (typeof window === "undefined") return false;
  const key = getAppointmentForceShowFrontendHiddenDoctorsStorageKey(userId);
  if (!key) return false;
  const raw = localStorage.getItem(key);
  if (raw === null) return false;
  return raw === "true";
}

export function persistForceShowFrontendHiddenDoctorsPreference(
  checked,
  userId = getCurrentAppointmentUserId()
) {
  if (typeof window === "undefined") return;
  const key = getAppointmentForceShowFrontendHiddenDoctorsStorageKey(userId);
  if (!key) return;
  localStorage.setItem(key, String(checked));
}

/**
 * 解析要顯示的醫師 id：預設全選；僅排除使用者明確取消勾選的醫師。
 * 新增醫師會自動顯示；已排除的醫師若再次出現在清單中仍維持不選。
 * @param {number[]} allDoctorIds 目前可選醫師 id（已依封存設定過濾）
 */
export function resolveVisibleDoctorIds(
  allDoctorIds,
  userIdOrScope = getCurrentAppointmentUserId(),
  brandId,
  branchId
) {
  const scope = normalizeAppointmentResourceScope(
    userIdOrScope,
    brandId,
    branchId
  );
  const excluded = readSavedExcludedDoctorIds(
    scope.userId,
    allDoctorIds,
    scope.brandId,
    scope.branchId
  );
  return resolveVisibleDoctorIdsFromExcluded(allDoctorIds, excluded);
}

function readScopedOnlyDoctorsWithAppointments(userId) {
  const key = getAppointmentOnlyDoctorsWithAppointmentsStorageKey(userId);
  if (!key) return null;
  return localStorage.getItem(key);
}

function normalizeDoctorResourceFilterMode(raw) {
  if (
    raw === APPOINTMENT_DOCTOR_RESOURCE_FILTER_MODES.WITH_APPOINTMENTS ||
    raw === "true"
  ) {
    return APPOINTMENT_DOCTOR_RESOURCE_FILTER_MODES.WITH_APPOINTMENTS;
  }
  if (
    raw === APPOINTMENT_DOCTOR_RESOURCE_FILTER_MODES.ALL ||
    raw === "false"
  ) {
    return APPOINTMENT_DOCTOR_RESOURCE_FILTER_MODES.ALL;
  }
  if (raw === APPOINTMENT_DOCTOR_RESOURCE_FILTER_MODES.ON_DUTY) {
    return APPOINTMENT_DOCTOR_RESOURCE_FILTER_MODES.ON_DUTY;
  }
  return null;
}

/** @deprecated 請改用 readDoctorResourceFilterModePreference */
export function readOnlyDoctorsWithAppointmentsPreference(
  userId = getCurrentAppointmentUserId()
) {
  return (
    readDoctorResourceFilterModePreference(userId) ===
    APPOINTMENT_DOCTOR_RESOURCE_FILTER_MODES.WITH_APPOINTMENTS
  );
}

export function readDoctorResourceFilterModePreference(
  userId = getCurrentAppointmentUserId()
) {
  if (typeof window === "undefined") {
    return DEFAULT_APPOINTMENT_DOCTOR_RESOURCE_FILTER_MODE;
  }

  const scoped = readScopedOnlyDoctorsWithAppointments(userId);
  const scopedMode = normalizeDoctorResourceFilterMode(scoped);
  if (scopedMode) {
    if (
      scoped === "true" ||
      scoped === "false"
    ) {
      const key = getAppointmentOnlyDoctorsWithAppointmentsStorageKey(userId);
      if (key) localStorage.setItem(key, scopedMode);
    }
    return scopedMode;
  }

  const legacy = localStorage.getItem(
    LEGACY_APPOINTMENT_ONLY_DOCTORS_WITH_APPOINTMENTS_KEY
  );
  const legacyMode = normalizeDoctorResourceFilterMode(legacy);
  if (legacyMode) {
    const key = getAppointmentOnlyDoctorsWithAppointmentsStorageKey(userId);
    if (key) {
      localStorage.setItem(key, legacyMode);
      localStorage.removeItem(LEGACY_APPOINTMENT_ONLY_DOCTORS_WITH_APPOINTMENTS_KEY);
    }
    return legacyMode;
  }

  return DEFAULT_APPOINTMENT_DOCTOR_RESOURCE_FILTER_MODE;
}

/** @deprecated 請改用 persistDoctorResourceFilterModePreference */
export function persistOnlyDoctorsWithAppointmentsPreference(
  checked,
  userId = getCurrentAppointmentUserId()
) {
  persistDoctorResourceFilterModePreference(
    checked
      ? APPOINTMENT_DOCTOR_RESOURCE_FILTER_MODES.WITH_APPOINTMENTS
      : APPOINTMENT_DOCTOR_RESOURCE_FILTER_MODES.ALL,
    userId
  );
}

export function persistDoctorResourceFilterModePreference(
  mode,
  userId = getCurrentAppointmentUserId()
) {
  if (typeof window === "undefined") return;
  const key = getAppointmentOnlyDoctorsWithAppointmentsStorageKey(userId);
  if (!key) return;
  const normalized =
    normalizeDoctorResourceFilterMode(mode) ||
    DEFAULT_APPOINTMENT_DOCTOR_RESOURCE_FILTER_MODE;
  localStorage.setItem(key, normalized);
  localStorage.removeItem(LEGACY_APPOINTMENT_ONLY_DOCTORS_WITH_APPOINTMENTS_KEY);
}

export function readOnlyConsultingRoomsWithAppointmentsPreference(
  userId = getCurrentAppointmentUserId()
) {
  if (typeof window === "undefined") return true;
  const key = getAppointmentOnlyConsultingRoomsWithAppointmentsStorageKey(userId);
  if (!key) return true;
  const raw = localStorage.getItem(key);
  if (raw === null) return true;
  return raw === "true";
}

export function persistOnlyConsultingRoomsWithAppointmentsPreference(
  checked,
  userId = getCurrentAppointmentUserId()
) {
  if (typeof window === "undefined") return;
  const key = getAppointmentOnlyConsultingRoomsWithAppointmentsStorageKey(userId);
  if (!key) return;
  localStorage.setItem(key, String(checked));
}

/** 清除舊版全站 key（登出時可呼叫） */
export function clearLegacyAppointmentDoctorPreferences() {
  if (typeof window !== "undefined") {
    localStorage.removeItem(LEGACY_APPOINTMENT_ONLY_DOCTORS_WITH_APPOINTMENTS_KEY);
  }
}
